import { JsonPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AssetDto, GalleryDto } from '@picflow/types';
import { billingScopes } from '../../../billing-consts';
import { CSV } from '../../../classes/csv.class';
import { SwitchComponent } from '../../../components/switch/switch.component';
import { defaultColorLabels } from '../../../configs/default-color-labels';
import { getAssetStatusName } from '../../../gallery/pipes/asset-status-name.pipe';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { AnalyticsService } from '../../../services/analytics.service';
import { AssetService } from '../../../services/asset.service';
import { OnGetData } from '../../../services/dialog.service';
import { ExifService, NormalizedExif } from '../../../services/exif.service';
import { ExportGalleryService } from '../../../services/export-gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { PrintOverviewService } from '../../../services/print-overview.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { DialogModeSwitcherComponent } from '../../../shared-extra/components/dialog-mode-switcher/dialog-mode-switcher.component';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { RadioButtonComponent } from '../../../shared-extra/components/radio-button/radio-button.component';
import { RadioComponent } from '../../../shared-extra/components/radio/radio.component';
import { CopyClipboardDirective } from '../../../shared-extra/directives/copy-to-clipboard';
import { InputPostDirective } from '../../../shared-extra/directives/input-post.directive';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';

@Component({
  selector: 'app-export-gallery-dialog',
  templateUrl: './export-gallery-dialog.component.html',
  styleUrls: ['./export-gallery-dialog.component.scss'],
  standalone: true,
  imports: [
    ResizeDirective,
    DialogModeSwitcherComponent,
    RadioComponent,
    TooltipDirective,
    SvgComponent,
    ButtonDirective,
    CopyClipboardDirective,
    CapitalizePipe,
    ContextMenuComponent,
    InputFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    InputPostDirective,
    RadioButtonComponent,
    JsonPipe,
    SwitchComponent,
  ],
})
export class ExportGalleryDialogComponent implements OnGetData {
  private assetService = inject(AssetService);
  private state = inject(StateService);
  private analytics = inject(AnalyticsService);
  private toasts = inject(ToastService);
  private exifService = inject(ExifService);
  private permissionService = inject(PermissionService);
  private printOverviewService = inject(PrintOverviewService);
  private exportGalleryService = inject(ExportGalleryService);
  dialogName = 'export-gallery';
  gallery: GalleryDto;
  close: (...args) => void;
  assets: AssetDto[];
  allAssets: AssetDto[];
  mode: 'filenames' | 'csv' | 'pdf' = 'filenames';
  export: 'everything' | 'selection' = 'everything';
  csvIncludeOptions = ['reviews', 'description', 'metadata'] as const;
  enabledCsvIncludeOptions: { [key: string]: boolean } = {
    reviews: true,
    description: true,
    metadata: true,
  };
  pdfIncludeOptions = ['filename', 'status', 'labels', 'favorites'] as const;
  enabledPdfIncludeOptions: { [key: string]: boolean } = { filename: true };
  thumbnailSize: 'small' | 'medium' | 'large' = 'medium';
  filenamesContent = '';
  containsSpaces: boolean;
  allowSelection: boolean;
  filenameForm = new FormGroup({
    extension: new FormControl(''),
    quotes: new FormControl(false),
  });

  modes = [
    {
      key: 'filenames',
      text: 'Filenames',
    },
    {
      key: 'pdf',
      text: 'Save PDF',
    },
    {
      key: 'csv',
      text: 'Export CSV',
    },
  ];

  filenamesOptions: {
    separator: 'comma' | 'or' | 'space';
  } = {
    separator: 'comma',
  };

  dialogOptions(): DialogOptions {
    return { size: 560, spacingX: 40, spacingY: 40, hideClose: true, minHeight: 525 };
  }

  async onGetData(data: { gallery: GalleryDto; mode: typeof this.mode }, close: (...args) => void) {
    this.gallery = data.gallery;
    if (data.mode) this.mode = data.mode;
    this.close = close;
    this.allAssets = await this.assetService.getAssetsForGallery(this.gallery.id);
    this.allowSelection = this.getAllowSelection();
    this.setExport(this.allowSelection ? 'selection' : 'everything');
  }

  setExport(value: 'everything' | 'selection') {
    this.export = value;
    this.setAssets();
    this.calcOutput();
  }

  setSeparator(separator: 'comma' | 'or' | 'space') {
    this.filenamesOptions.separator = separator;

    // Set quotes to true in some cases because of this testing
    // https://docs.google.com/spreadsheets/d/1zmVLxTBbhzQFko3yYUzewL1aMkZHlHu-kCMLlKDRkks/edit#gid=0
    if (['or', 'space'].includes(separator)) {
      this.filenameForm.controls.quotes.setValue(true);
    } else {
      this.filenameForm.controls.quotes.setValue(false);
    }

    this.calcOutput();
  }

  toggleCsvInclude(option: string) {
    this.enabledCsvIncludeOptions[option] = !this.enabledCsvIncludeOptions[option];
    this.calcOutput();
  }

  togglePdfInclude(option: string) {
    this.enabledPdfIncludeOptions[option] = !this.enabledPdfIncludeOptions[option];
    this.calcOutput();
  }

  setThumbnail(size: 'small' | 'medium' | 'large') {
    this.thumbnailSize = size;
    this.calcOutput();
  }

  setAssets() {
    if (this.allowSelection && this.export === 'selection') {
      this.assets = this.selectedAssets;
    } else {
      this.assets = this.allAssets;
    }
  }

  calcOutput() {
    if (this.mode === 'filenames') {
      const extension = this.filenameForm.value.extension.replace(/^\./, '');
      const names = this.assets.map(asset => {
        const name = asset.version_data.asset_name;
        if (extension === '*') return name;
        if (!extension) return name.replace(/\.[^/.]+$/, '');
        return name.replace(/\.[^/.]+$/, '') + '.' + extension;
      });
      if (this.filenamesOptions.separator === 'comma') {
        if (this.filenameForm.value.quotes) {
          this.filenamesContent = names.map(name => `"${name}"`).join(', ');
        } else {
          this.filenamesContent = names.join(', ');
        }
      } else if (this.filenamesOptions.separator === 'or') {
        if (this.filenameForm.value.quotes) {
          this.filenamesContent = names.map(name => `"${name}"`).join(' OR ');
        } else {
          this.filenamesContent = names.join(' OR ');
        }
      } else {
        if (this.filenameForm.value.quotes) {
          this.filenamesContent = names.map(name => `"${name}"`).join(' ');
        } else {
          this.filenamesContent = names.join(' ');
        }
      }
      this.containsSpaces = names.some(name => name.includes(' '));
    }
  }

  onFilenamesCopied() {
    this.analytics.track('filenames_copied');
    this.toasts.add('File names copied to clipboard!', 'success');
  }

  print() {
    this.printOverviewService.print({
      gallery: this.gallery,
      assets: this.assets,
      favorites: this.enabledPdfIncludeOptions.favorites,
      filename: this.enabledPdfIncludeOptions.filename,
      color_labels: this.enabledPdfIncludeOptions.labels,
      status: this.enabledPdfIncludeOptions.status,
      thumbnailSize: this.thumbnailSize,
    });
  }

  exportCsv() {
    const header = ['Name', 'Upload at'];
    const csvRows = [];

    const enabled = this.enabledCsvIncludeOptions;

    if (enabled.description) header.push('Description');

    if (enabled.reviews) {
      header.push('Status');
      header.push('Label');
      header.push('Favorites');
    }

    if (enabled.metadata) {
      header.push('Camera');
      header.push('Lens');
      header.push('Aperture');
      header.push('Shutter Speed');
      header.push('Focal Length');
      header.push('ISO');
      header.push('Captured At');
    }

    let colorLabels = this.gallery.color_labels;
    if (!this.permissionService.canDisplayElement(billingScopes.asset.customLabels)) colorLabels = defaultColorLabels;

    this.assets.forEach(asset => {
      const exif = this.exifService.getExif(asset) || <NormalizedExif>{};
      const row = [asset.version_data.asset_name, new Date(asset.created_at * 1000).toLocaleDateString()];
      if (enabled.description) row.push(asset.description || exif.description);

      if (enabled.reviews) {
        row.push(getAssetStatusName(asset.status));
        if (asset.color_label) row.push(colorLabels.find(l => l.color === asset.color_label)?.name);
        else row.push('');
        row.push(asset.likes.toString());
      }

      if (enabled.metadata) {
        row.push(exif.camera);
        row.push(exif.lens);
        row.push(exif.aperture);
        row.push(exif.shutterSpeed);
        row.push(exif.focalLength);
        row.push(exif.iso);
        row.push(exif.captureTime ? new Date(exif.captureTime).toLocaleDateString() : '');
      }
      csvRows.push(row);
    });

    new CSV()
      .setHeaders(header)
      .addRows(csvRows)
      .download(`${this.gallery.name || this.gallery.title || 'Untitled'}.csv`);
  }

  private getAllowSelection() {
    if (this.exportGalleryService.currentSelectedAssets?.length) return true;
    if (!this.exportGalleryService.currentFilteredAssets?.length) return false;
    return this.exportGalleryService.currentFilteredAssets.length !== this.allAssets.length;
  }

  private get selectedAssets() {
    if (this.exportGalleryService.currentSelectedAssets?.length) {
      return this.exportGalleryService.currentSelectedAssets;
    }
    if (this.exportGalleryService.currentFilteredAssets?.length) {
      return this.exportGalleryService.currentFilteredAssets;
    }
  }

  public getFileNameOptionsText() {
    if (this.filenamesOptions.separator === 'comma') return 'Comma Separator';
    if (this.filenamesOptions.separator === 'or') return 'OR Separator';
    return 'No Separator';
  }
}
